import type { ReactNode } from "react";
import { FilterChips, Input } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import type { ListFilterDef, ListFilterDefs, ListFilters } from "@/hooks/useListFilters";

// The presentational half of the descriptor-driven filter shell. Pure layout +
// controls: all state, parsing and URL syncing live in useListFilters, so this
// component is as domain-free as the hook — it renders whatever the descriptor
// declares and nothing else.
//
// DELIBERATELY NOT components/layout/FilterToolbar, and deliberately not built
// on top of it. FilterToolbar is a card (border, shadow, its own padding and
// bottom margin) and measures its children with a ResizeObserver, imperatively
// setting `display: none` on whatever it decides has overflowed and moving it
// behind a "More filters" disclosure. Both are wrong here: the card would
// restyle every page that adopts this shell, and width-dependent hiding is a
// poor foundation for a mechanism several list pages are about to depend on
// (in jsdom, where every measured width is 0, it hides everything). The two are
// not in conflict — a page that wants that chrome can pass these controls into
// FilterToolbar's `filters` slots — they just should not be the same component.

/**
 * One horizontal band of the bar. `keys` are descriptor keys, rendered left to
 * right; `before`/`after` are escape hatches for controls the descriptor does
 * not own (a scope toggle, an export button).
 */
export interface ListFilterRow {
  keys?: string[];
  /**
   * Which of this row's `keys` render as §9.1 CHIPS rather than as a `<select>`.
   *
   * 🔴 OPT-IN, and deliberately not a property of the descriptor. Table and Tile
   * Standard §9.1 replaces a dropdown with chips for a *short closed vocabulary*
   * — status, verdict, category — where the option set is itself information,
   * and explicitly preserves the `<select>` for "a genuinely long, unbounded or
   * free-text-searchable option list". That distinction is about the OPTION
   * COUNT, so it cannot be inferred from the control kind, and deriving it from
   * a length cutoff here would silently restyle every page that mounts this bar
   * on the day someone adds a twenty-first US state. A row that names no chip
   * keys renders exactly what it rendered before.
   *
   * The chip group keeps the descriptor's `label` as its `aria-label`, so the
   * accessible name of the control is unchanged by the swap — only the shape is.
   */
  chipKeys?: string[];
  /**
   * Counts for the chip pills: `{ <filterKey>: { <optionValue>: count } }`.
   * Optional per key and per option — a chip with no count renders bare rather
   * than as a zero, because "none here" and "not counted" are different claims.
   */
  chipCounts?: Record<string, Record<string, number> | undefined>;
  before?: ReactNode;
  after?: ReactNode;
  /** Overrides the row's flex container classes. */
  className?: string;
}

interface ListFilterBarProps<D extends ListFilterDefs> {
  filters: ListFilters<D>;
  rows: ListFilterRow[];
  className?: string;
}

const SELECT_CLASS =
  "h-10 rounded-md border border-border-light bg-white-pure px-space-2 text-small";

const ROW_CLASS = "flex flex-wrap items-center gap-space-3";

export function ListFilterBar<D extends ListFilterDefs>({
  filters,
  rows,
  className,
}: ListFilterBarProps<D>) {
  return (
    <div className={cn("flex flex-col gap-space-3 mb-space-3", className)}>
      {rows.map((row, index) => (
        <div key={index} className={row.className ?? ROW_CLASS}>
          {row.before}
          {(row.keys ?? []).map((key) => (
            <ListFilterControl
              key={key}
              filters={filters}
              name={key}
              asChips={row.chipKeys?.includes(key) ?? false}
              counts={row.chipCounts?.[key]}
            />
          ))}
          {row.after}
        </div>
      ))}
    </div>
  );
}

/**
 * The token a chip group compares against to decide which chip is active.
 *
 * 🔴 It has to be the SERIALISED token, not `String(value)`. A boolean filter
 * declares `serialize: (v) => (v ? "1" : null)` against options `""` / `"1"`, so
 * `String(false)` is `"false"` — a value matching no option at all. The
 * `<select>` path gets away with that because a browser falls back to showing
 * the first option when `value` matches none; a chip group has no such fallback
 * and would render with nothing active.
 */
function activeToken(def: ListFilterDef<unknown>, shown: unknown): string {
  if (def.serialize) return def.serialize(shown) ?? "";
  if (shown === undefined || shown === null) return "";
  return String(shown);
}

interface ListFilterControlProps<D extends ListFilterDefs> {
  filters: ListFilters<D>;
  name: string;
  asChips: boolean;
  counts?: Record<string, number>;
}

function ListFilterControl<D extends ListFilterDefs>({
  filters,
  name,
  asChips,
  counts,
}: ListFilterControlProps<D>) {
  const def = filters.defs[name];
  if (!def || def.control === "hidden") return null;

  // The descriptor is keyed by plain strings while `values`/`set` are typed
  // against the descriptor's own keys. This is the one seam where the two meet;
  // the cast is contained here rather than leaking into either half's API.
  const setValue = (value: unknown) =>
    (filters.set as (key: string, value: unknown) => void)(name, value);
  const shown = (filters.inputs as Record<string, unknown>)[name];

  if (def.control === "search") {
    return (
      <Input
        type="search"
        placeholder={def.placeholder}
        aria-label={def.label}
        value={(shown as string | undefined) ?? ""}
        onChange={(e) => setValue(e.target.value)}
        className={def.className}
      />
    );
  }

  if (asChips) {
    return (
      <FilterChips
        chips={(def.options ?? []).map((option) => ({
          id: option.value,
          label: option.label,
          count: counts?.[option.value],
        }))}
        activeId={activeToken(def, shown)}
        onChange={(id) =>
          setValue((filters.parseValue as (key: string, raw: string) => unknown)(name, id))
        }
        // The descriptor's own label, so the accessible name of this filter is
        // identical whether it renders as chips or as a select.
        label={def.label ?? name}
        // `def.className` is deliberately NOT forwarded: it carries the fixed
        // width a `<select>` needs (`w-48`), which would squash a group that is
        // supposed to wrap to its content.
      />
    );
  }

  return (
    <select
      className={cn(SELECT_CLASS, def.className)}
      value={String(shown ?? "")}
      onChange={(e) =>
        setValue((filters.parseValue as (key: string, raw: string) => unknown)(name, e.target.value))
      }
      aria-label={def.label}
    >
      {(def.options ?? []).map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
